import React from "react";

import SimpleSelect from "@/mk2/apps/bazaar/components/SimpleSelect";
import { Review } from "@/mk2/apps/strollers/components/Review";
import { StrollerReviewsHeader } from "@/mk2/apps/strollers/containers/StrollerReviews/StrollerReviewsHeader";
import { Props } from "@/mk2/apps/strollers/containers/StrollerReviews/StrollerReviewsPage";
import {
    strollersAllReviewsGalleryUrl,
    strollersAllReviewsPhotosUrl,
} from "@/mk2/apps/strollers/urls";
import { PhotosRowOfSquares } from "@/mk2/components/PhotosRowOfSquares";
import { LoadingSwitch } from "@/mk2/containers/LoadingSwitch/LoadingSwitch";
import { url } from "@/mk2/helpers/urls";
import {
    PhotoEntity,
    ProductEntity,
    StrollerUserReviewEntity,
    StrollerUserReviewPhotoEntity,
} from "@/mk2/schemas";
import {
    PHOTOS_FROM_USERS_RATINGS,
    REVIEWS_OF_ALIKE_MODELS,
    REVIEWS_OF_ALIKE_MODELS_EXPLANATION,
    REVIEW_ARTICLE_SORT_EXPERIENCES_BY_BEST,
    REVIEW_ARTICLE_SORT_EXPERIENCES_BY_LIKES,
    REVIEW_ARTICLE_SORT_EXPERIENCES_BY_NEWEST,
    REVIEW_ARTICLE_SORT_EXPERIENCES_BY_PHOTOS,
    REVIEW_ARTICLE_SORT_EXPERIENCES_BY_WORST,
} from "@/mk/autogenerated/translations/StrollerReviews.03c944147020cd1ae6f13342e01389fb";

import styles from "./StrollerReviews.mscss";

enum SortReviewsBy {
    NEWEST = "newest",
    BEST = "best",
    WORST = "worst",
    PHOTOS = "photos",
    LIKES = "likes",
}

const sortOptions: Record<SortReviewsBy, string> = {
    [SortReviewsBy.NEWEST]: REVIEW_ARTICLE_SORT_EXPERIENCES_BY_NEWEST,
    [SortReviewsBy.BEST]: REVIEW_ARTICLE_SORT_EXPERIENCES_BY_BEST,
    [SortReviewsBy.WORST]: REVIEW_ARTICLE_SORT_EXPERIENCES_BY_WORST,
    [SortReviewsBy.LIKES]: REVIEW_ARTICLE_SORT_EXPERIENCES_BY_LIKES,
    [SortReviewsBy.PHOTOS]: REVIEW_ARTICLE_SORT_EXPERIENCES_BY_PHOTOS,
};

const sortingFns: Record<
    SortReviewsBy,
    (a: StrollerUserReviewEntity, b: StrollerUserReviewEntity) => number
> = {
    [SortReviewsBy.NEWEST]: (a, b) =>
        new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime(),
    [SortReviewsBy.BEST]: (a, b) =>
        b.score - a.score || new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime(),
    [SortReviewsBy.WORST]: (a, b) =>
        a.score - b.score || new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime(),
    [SortReviewsBy.PHOTOS]: (a, b) =>
        b.photos.length - a.photos.length ||
        new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime(),
    [SortReviewsBy.LIKES]: (a, b) =>
        b.likeable.likesCount - a.likeable.likesCount ||
        new Date(b.createdTime).getTime() - new Date(a.createdTime).getTime(),
};

interface State {
    sortBy: SortReviewsBy;
}

export class StrollerReviews extends React.Component<Props, State> {
    public state: State = {
        sortBy: SortReviewsBy.NEWEST,
    };

    public render() {
        const { loadingState, stroller } = this.props;
        return (
            <div className={styles.StrollerReviews}>
                <LoadingSwitch
                    loadingState={loadingState}
                    onLoad={this.load}
                    onRenderSuccess={this.renderLoadSuccess}
                    hasData={!!stroller}
                />
            </div>
        );
    }

    private renderLoadSuccess = () => {
        const {
            stroller,
            strollerAlikeStrollers,
            strollerReviews: unsortedStrollerReviews,
            strollerPendingReviews: unsortedStrollerPendingReviews,
            isAdmin,
            onToggleMenuMore,
            location,
        } = this.props;

        const isReviewFromAlikeModel = (r: StrollerUserReviewEntity) =>
            r.strollerId !== stroller.id;

        const strollerReviews = unsortedStrollerReviews.sort(sortingFns[this.state.sortBy]);
        const strollerPendingReviews = unsortedStrollerPendingReviews.sort(
            sortingFns[this.state.sortBy],
        );

        // v galeri zobrazujeme len fotky zo schvalenych recenzií
        const galleryPhotos: PhotoEntity[] = strollerReviews
            .reduce((ret, review) => [...ret, ...review.photos], []) // collect all review photos
            .map((photo: StrollerUserReviewPhotoEntity) => photo.photoFile); // extract photo file

        return (
            <div>
                <StrollerReviewsHeader
                    className={styles.StrollerReviews__header}
                    stroller={stroller}
                    showScore
                />

                {galleryPhotos.length > 0 && (
                    <div className={styles.StrollerReviews__gallery}>
                        <div>{PHOTOS_FROM_USERS_RATINGS}</div>

                        {galleryPhotos && (
                            <PhotosRowOfSquares
                                className={styles.StrollerReviews__gallery__photosRow}
                                photos={galleryPhotos}
                                photoVersion="s150x150"
                                maxWidth={400}
                                linkTo={this.imageLinkTo}
                                galleryLinkTo={this.galleryLinkTo}
                            />
                        )}
                    </div>
                )}

                {strollerReviews.length + strollerPendingReviews.length > 1 && (
                    <div className={styles.StrollerReviews__sorting}>
                        <SimpleSelect
                            type="bigger"
                            selected={this.state.sortBy}
                            options={sortOptions}
                            onChange={this.onSortChange}
                        />
                    </div>
                )}

                {
                    // poradie hodnoteni ma byt:
                    //   1. najskor schvalene hodnotenia z kocika 'stroller'
                    //   2. potom hotnonia 'strolleru' cakajuce na schvalenie
                    //   3. potom hodnotenia z pribuznych modelov
                }

                {strollerReviews
                    .filter((r) => !isReviewFromAlikeModel(r))
                    .map((review) => (
                        <div
                            id={`review-${review.id}`}
                            key={review.id}
                            className={styles.StrollerReviews__review}
                        >
                            <Review
                                stroller={stroller}
                                review={review}
                                reviewStroller={stroller}
                                product={this.findProduct(review)}
                                isAdmin={isAdmin}
                                onToggleMenuMore={onToggleMenuMore}
                                location={location}
                            />
                        </div>
                    ))}

                {strollerPendingReviews.map((review) => (
                    <div
                        id={`review-${review.id}`}
                        key={review.id}
                        className={styles.StrollerReviews__review}
                    >
                        <Review
                            stroller={stroller}
                            review={review}
                            reviewStroller={stroller}
                            product={this.findProduct(review)}
                            isAdmin={isAdmin}
                            onToggleMenuMore={onToggleMenuMore}
                            location={location}
                        />
                    </div>
                ))}

                {strollerReviews
                    .filter((r) => isReviewFromAlikeModel(r))
                    .map((review, idx) => {
                        const reviewStroller = strollerAlikeStrollers.find(
                            (s) => s.id === review.strollerId,
                        );
                        return (
                            <>
                                {idx === 0 && (
                                    <>
                                        <h2 className={styles.StrollerReviews__alike}>
                                            {REVIEWS_OF_ALIKE_MODELS}
                                        </h2>

                                        <div className={styles.StrollerReviews__alike_explanation}>
                                            {REVIEWS_OF_ALIKE_MODELS_EXPLANATION}
                                        </div>
                                    </>
                                )}

                                <div
                                    id={`review-${review.id}`}
                                    key={review.id}
                                    className={styles.StrollerReviews__review}
                                >
                                    <Review
                                        stroller={stroller}
                                        review={review}
                                        reviewStroller={reviewStroller}
                                        product={this.findProduct(review)}
                                        isAdmin={isAdmin}
                                        onToggleMenuMore={onToggleMenuMore}
                                        location={location}
                                    />
                                </div>
                            </>
                        );
                    })}
            </div>
        );
    };

    private findProduct = (review: StrollerUserReviewEntity): ProductEntity => {
        const { strollerReviewsActiveProducts } = this.props;
        return strollerReviewsActiveProducts.find((p: ProductEntity) => {
            return p.seller.user.id === review.user.id;
        });
    };

    private imageLinkTo = (photofile: PhotoEntity) => {
        const { stroller } = this.props;
        return url(strollersAllReviewsPhotosUrl, {
            slug: stroller.slug,
            photoId: photofile.id,
        });
    };

    private galleryLinkTo = (photofile: PhotoEntity) => {
        const { stroller } = this.props;
        return url(strollersAllReviewsGalleryUrl, {
            slug: stroller.slug,
        });
    };

    private load = () => {
        this.props.onLoad(this.props.strollerSlug);
    };

    private onSortChange = (value: SortReviewsBy) => {
        this.setState({
            sortBy: value,
        });
    };
}
